/**
 * Collection of helper utilities for color generation, lookup, and conversion.
 * @type {Object}
 * @example
 * import { colorHelpers } from './color.helpers';
 * const color = colorHelpers.getColorByIndex(2);
 */
export const colorHelpers = {
  /**
   * Generates a random hexadecimal color string.
   *
   * @returns {string} A hex color string (e.g. `"#a3f0c2"`).
   * @example
   * colorHelpers.generateRandomColor(); // → '#a3f0c2'
   */
  generateRandomColor: () => '#' + (Math.random() * 0xFFFFFF << 0).toString(16).padStart(6, '0'),
  /**
   * Returns a predefined color for indexes 0–5, or a random color for any other index.
   *
   * @param {number} index - Index to look up (0–5 map to fixed colors).
   * @returns {string} A hex color string.
   * @example
   * colorHelpers.getColorByIndex(0); // → '#52C41A'
   * colorHelpers.getColorByIndex(99); // → '#a3f0c2' (random)
   */
  getColorByIndex: (index) => {
    return {
      0: '#52C41A',
      1: '#1890FF',
      2: '#722ED1',
      3: '#FF7875',
      4: '#03AB93',
      5: '#EABD3B',
    }[index] || colorHelpers.generateRandomColor();
  },
  /**
   * Converts a hex color string to an `rgba()` CSS value.
   *
   * @param {string} hex - Hex color string without `#` prefix (e.g. `"52C41A"`).
   * @param {number} [alpha=1] - Opacity value between 0 and 1.
   * @returns {string} CSS `rgba()` string (e.g. `"rgba(82,196,26,1)"`).
   * @example
   * colorHelpers.hex2rgba('52C41A', 0.5); // → 'rgba(82,196,26,0.5)'
   */
  hex2rgba: (hex, alpha = 1) => {
    const [r, g, b] = hex.match(/\w\w/g).map(x => parseInt(x, 16));
    return `rgba(${r},${g},${b},${alpha})`;
  },
  /**
   * Deterministically maps a string key (e.g. an entity id) to one of the fixed
   * palette colors from `getColorByIndex`, so the same key always renders the
   * same color across renders/reloads regardless of array position.
   *
   * @param {string} key - Stable identifier to hash (e.g. an account id).
   * @returns {string} A hex color string from the fixed palette.
   * @example
   * colorHelpers.getColorForKey('acc_123'); // → '#1890FF'
   */
  getColorForKey: (key = '') => {
    const paletteSize = 6;
    let hash = 0;
    for (let i = 0; i < key.length; i++) {
      hash = (hash * 31 + key.charCodeAt(i)) | 0;
    }
    return colorHelpers.getColorByIndex(Math.abs(hash) % paletteSize);
  },
}
